import { ClaimReservationsServiceAPI } from '@API/data-services/claim-reservations-service'
import { Modal } from '@Components/common/modal'
import { Pagination } from '@Components/common/pagination'
import { usePaginatedState } from '@Components/common/use-paginated-state'
import { ClaimReservationResponse, ResponseError } from '@Generated/back-office-client/src'
import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { Flex, IconButton, Skeleton, Table, Text, TextField, Theme } from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { parseISO } from 'date-fns'
import { useMemo, useState } from 'react'
import { RootState } from '@AppState/redux-store'
import { useSelector } from 'react-redux'
import { convertDateToIsoDate, formatDateToMonthDayYearString } from '../util/utils'

const PAGE_SIZE = 20

// Results cover the current calendar year plus the three before it, and all future appointments.
const SEARCH_HISTORY_YEARS = 3

type PlayerSearchModalProps = {
  onResultSelected: (reservation: ClaimReservationResponse) => void
  onClose: () => void
}

export const PlayerSearchModal = ({ onResultSelected, onClose }: PlayerSearchModalProps) => {
  const [query, setQuery] = useState('')
  const [submittedQuery, setSubmittedQuery] = useState('')
  const [page, setPage] = usePaginatedState()

  const { activeCustomerMetadata } = useSelector((state: RootState) => state.users)
  const timezone = activeCustomerMetadata?.timezone ?? 'UTC'

  const searchStartDate = useMemo(
    () => convertDateToIsoDate(new Date(new Date().getFullYear() - SEARCH_HISTORY_YEARS, 0, 1)),
    [],
  )

  const {
    data: searchResults,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['player-search', submittedQuery, page],
    queryFn: () =>
      ClaimReservationsServiceAPI.searchReservations(
        submittedQuery,
        page * PAGE_SIZE,
        (page + 1) * PAGE_SIZE,
        searchStartDate,
        true,
      ),
    enabled: !!submittedQuery,
  })

  const errorMessage = useMemo(() => {
    if (!error) {
      return null
    }
    if (error instanceof ResponseError) {
      return error.message
    }
    return 'An unexpected error occurred.'
  }, [error])

  const handleSearch = () => {
    const trimmed = query.trim()
    if (!trimmed) {
      return
    }
    setSubmittedQuery(trimmed)
    setPage(0)
  }

  return (
    <Modal
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
      title="Player Appointment Search"
      description="Locate an appointment by searching below."
      className="player-search-modal">
      <Flex direction="column" gap="3">
        <Flex direction="column" gap="1">
          <Text size="5" weight="bold">
            Player Appointment Search
          </Text>
          <Text size="2" color="gray">
            Locate an appointment by searching below.
          </Text>
        </Flex>

        <TextField.Root
          placeholder="Search by name or phone number"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearch()
            }
          }}>
          <TextField.Slot side="right">
            <IconButton
              size="1"
              variant="ghost"
              disabled={!query.trim()}
              onClick={handleSearch}
              aria-label="Search">
              <MagnifyingGlassIcon height="16" width="16" />
            </IconButton>
          </TextField.Slot>
        </TextField.Root>

        {errorMessage ? <Text color="red">{errorMessage}</Text> : null}

        {submittedQuery && !isLoading && searchResults?.totalResults === 0 && !errorMessage ? (
          <Text>No results found.</Text>
        ) : null}

        {submittedQuery && (isLoading || (searchResults && searchResults.totalResults > 0)) ? (
          <>
            <Skeleton loading={isLoading}>
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Phone</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell># of Tickets</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Date</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Time</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Claim Center</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {searchResults?.results.map((result: ClaimReservationResponse) => (
                    <Table.Row key={result.id}>
                      <Table.Cell>{result.claimReservationDetails.name}</Table.Cell>
                      <Table.Cell>{result.claimReservationDetails.phone ?? '\u2014'}</Table.Cell>
                      <Table.Cell>{result.claimReservationDetails.numberOfTickets}</Table.Cell>
                      <Table.Cell>
                        {formatDateToMonthDayYearString(
                          parseISO(result.timeslot.display.localDate),
                          timezone,
                        )}
                      </Table.Cell>
                      <Table.Cell>{result.timeslot.display.localTime}</Table.Cell>
                      <Table.Cell>{result.claimCenter.name}</Table.Cell>
                      <Table.Cell>
                        {result.claimReservationDetails.isCancelled ? (
                          <Text color="gray">Cancelled</Text>
                        ) : (
                          <Text
                            color="red"
                            weight="medium"
                            style={{ cursor: 'pointer' }}
                            onClick={() => onResultSelected(result)}>
                            Open Details
                          </Text>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Skeleton>
            {searchResults?.totalResults ? (
              // Claims page sets a yellow accent; pagination uses the app's red so it stays readable
              <Theme accentColor="red" hasBackground={false}>
                <Pagination
                  totalItems={searchResults.totalResults}
                  pageSize={PAGE_SIZE}
                  currentPage={page}
                  onPageChange={setPage}
                />
              </Theme>
            ) : null}
          </>
        ) : null}
      </Flex>
    </Modal>
  )
}
